import buildPropertyRatesKey from 'lib/offer/buildPropertyRatesKey'

interface Params {
  propertyId: string | undefined
  checkIn: string | undefined
  checkOut: string | undefined
  occupancies: Array<App.Occupants>
}

/**
 * Whether the cached dated property-rate availability for this tile contains at least one rate with a
 * loyalty package-upgrade relation.
 *
 * Reads the *raw* cached rates only — no enrichment / origin-target resolution — because the search
 * banner is an offer-level teaser (a boolean "an upgrade exists for these dates"), not a rate-specific
 * inclusions modal. This deliberately avoids the offer-page-coupled `getPropertyRatesByRoom`.
 *
 * @NOTE The cache key must be built with the same params `fetchPropertyRates` uses (utmContent /
 * roomTypeId / roomRateId default to `''`), or it won't match the stored record.
 */
function getSearchTileHasLoyaltyPackageUpgrade(
  state: App.State,
  { propertyId, checkIn, checkOut, occupancies }: Params,
): boolean {
  const key = buildPropertyRatesKey({
    propertyId,
    checkIn,
    checkOut,
    occupancies,
    utmContent: undefined,
    roomTypeId: undefined,
    roomRateId: undefined,
  })
  if (!key) return false

  const rooms = state.offer.propertyRates[key]?.rooms
  if (!rooms) return false

  return rooms.some((room) => {
    if (!('rates' in room)) return false
    // Mirror the offer page: a rate only renders (and can offer its upgrade) when it's in one of the
    // room's display lists — `getPropertyRatesByRoom` builds its rendered `ordered*Rates` exclusively
    // from `room.lists.hotelOnly`/`room.lists.hotelWithFlights`. Relations on unlisted rates — e.g. an
    // unlisted property-collect variant — must not light the banner for an upgrade the offer page will
    // never show.
    const listedRateIds = new Set([...room.lists.hotelOnly, ...room.lists.hotelWithFlights].map(({ id }) => id))
    return Object.values(room.rates).some(
      (rate) => listedRateIds.has(rate.rateId) && (rate.relations?.upgradeTargets?.package?.length ?? 0) > 0,
    )
  })
}

export default getSearchTileHasLoyaltyPackageUpgrade
